import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CalendarDays, FileText, HandCoins, Inbox, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/components/site/Reveal";
import { AnnouncementsFeed } from "@/components/portal/AnnouncementsFeed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_LINKS = [
  { name: "Gmail", url: "https://mail.google.com" },
  { name: "Google Drive", url: "https://drive.google.com" },
  { name: "Google Calendar", url: "https://calendar.google.com" },
  { name: "HAG Carpets", url: "https://www.hagcarpets.com" },
  { name: "Titas Footwear", url: "https://www.titasindia.com" },
  { name: "Youvora", url: "https://www.youvora.com" },
  { name: "Ag Perfume", url: "https://www.agperfume.com" },
  { name: "Nasir Biri", url: "https://www.nasirbiri.com" },
  { name: "Eskay Biri", url: "https://www.eskaybiri.com" },
  { name: "Arcadia", url: "https://myflexworx.com" },
];

const NAV_CARDS = [
  { to: "/portal/inquiries", icon: Inbox, title: "Inquiries & Applications", text: "Contact inquiries and career applications" },
  { to: "/portal/requests", icon: HandCoins, title: "Leave & Loans", text: "Submit and track your requests" },
  { to: "/portal/holidays", icon: CalendarDays, title: "Holidays", text: "Company holiday calendar" },
  { to: "/portal/documents", icon: FileText, title: "Documents", text: "Policies, forms and circulars" },
];

const fmtHoliday = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

export default function Portal() {
  usePageMeta("Employee Portal — SKN Group", "SKN Group employee portal.");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/holidays`, { withCredentials: true });
        const today = new Date().toISOString().slice(0, 10);
        if (active) setUpcoming(res.data.filter((h) => h.date >= today).slice(0, 3));
      } catch (err) {
        console.error("Failed to load holidays", err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="portal-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Welcome */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-foreground/10 pb-10">
          <div className="flex items-center gap-5">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                referrerPolicy="no-referrer"
                className="w-14 h-14 rounded-full border border-[#B8997A]/50"
                data-testid="portal-user-avatar"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#B8997A]/20 flex items-center justify-center font-display text-xl bronze">
                {user?.name?.[0] || "E"}
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A]">
                Employee Portal{user?.is_manager ? " · Manager" : ""}
              </p>
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight" data-testid="portal-welcome-name">
                Welcome, {user?.name || "Employee"}
              </h1>
              <p className="text-sm text-muted-foreground mt-1" data-testid="portal-user-email">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="portal-logout-button"
            className="inline-flex items-center gap-2 border border-foreground/30 px-6 py-3 text-sm hover:border-[#B8997A] hover:text-[#B8997A] transition-colors duration-300 shrink-0 self-start md:self-auto"
          >
            <LogOut size={15} strokeWidth={1.5} /> Log Out
          </button>
        </div>

        {/* Nav cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border mt-12" data-testid="portal-nav-cards">
          {NAV_CARDS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`portal-nav-${c.to.split("/").pop()}`}
              className="group bg-background p-6 hover:bg-secondary transition-colors duration-300"
            >
              <c.icon size={20} strokeWidth={1.25} className="bronze" />
              <p className="mt-4 font-display text-lg font-medium tracking-tight flex items-center justify-between">
                {c.title}
                <ArrowUpRight size={15} strokeWidth={1.5} className="text-muted-foreground group-hover:text-[#B8997A] transition-colors" />
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{c.text}</p>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          {/* Announcements */}
          <div className="lg:col-span-8">
            <AnnouncementsFeed isManager={!!user?.is_manager} />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-12">
            <div data-testid="portal-upcoming-holidays">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8997A]">Upcoming Holidays</h2>
                <Link to="/portal/holidays" data-testid="portal-holidays-link" className="text-xs text-muted-foreground hover:text-[#B8997A] transition-colors">
                  View all
                </Link>
              </div>
              <div className="space-y-px bg-border border border-foreground/10">
                {upcoming.length === 0 ? (
                  <div className="bg-background p-6 text-center text-sm text-muted-foreground">No upcoming holidays.</div>
                ) : (
                  upcoming.map((h) => (
                    <div key={h.id} className="bg-background p-4 flex items-center gap-3" data-testid={`upcoming-holiday-${h.id}`}>
                      <CalendarDays size={16} strokeWidth={1.25} className="bronze shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{fmtHoliday(h.date)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">Quick Links</h2>
              <div className="grid grid-cols-2 gap-px bg-border" data-testid="portal-quick-links">
                {QUICK_LINKS.map((l) => (
                  <a
                    key={l.name}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`portal-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group bg-background p-4 hover:bg-secondary transition-colors duration-300"
                  >
                    <p className="font-medium text-xs flex items-center justify-between">
                      {l.name}
                      <ArrowUpRight size={12} strokeWidth={1.5} className="text-muted-foreground group-hover:text-[#B8997A] transition-colors" />
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
